import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import LOCATIONS from '../constants/locations';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import { normalizeUAEPhone, isUAEPhoneValid } from '../utils/phone';


export default function CompanySignup() {
    const { signup } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || '/';

    const [form, setForm] = useState({
        email: '', password: '', first_name: '', last_name: '',
        role: 'company', phone: '', location: '',
        address_line: '', building: '', unit: ''
    });

    const [phoneDisplay, setPhoneDisplay] = useState('');

    const handlePhone = (e) => {
        const { display, stored } = normalizeUAEPhone(e.target.value);
        setPhoneDisplay(display);
        setForm(prev => ({ ...prev, phone: stored }));
    };

    const phoneValid = isUAEPhoneValid(phoneDisplay);

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!agreed) {
            setError('Please agree to the Terms & Conditions and Privacy Policy to continue.');
            return;
        }
        if (!phoneValid) {
            setError('Please enter a valid UAE phone number.');
            return;
        }
        setError('');
        setLoading(true);
        try {
            const result = await signup(form);
            if (result.needsEmailConfirmation) {
                setSubmitted(true);
            } else {
                navigate(from, { replace: true });
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Signup failed');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-sand-50 px-4">
                <div className="w-full max-w-sm text-center">
                    <div className="w-12 h-12 rounded-full bg-terra-400/10 flex items-center justify-center mx-auto mb-4">
                        <svg className="w-6 h-6 text-terra-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                        </svg>
                    </div>
                    <p className="font-heading text-xl font-semibold text-ink-900 mb-2">Check your email</p>
                    <p className="text-sm text-ink-400 leading-relaxed mb-6">
                        We've sent a confirmation link to <span className="text-ink-700 font-medium">{form.email}</span>. Click it to activate your account, then sign in.
                    </p>
                    <Link to="/login" className="text-sm text-terra-400 font-medium hover:text-terra-500 transition-colors">
                        Go to sign in
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex bg-sand-50">

            {/* Left brand panel — desktop only */}
            <div className="hidden lg:flex flex-col justify-center px-16 bg-terra-400/10 border-r border-sand-200 w-2/5 shrink-0">
                <p className="font-heading text-4xl font-bold text-ink-900 mb-4 leading-tight">
                    Start offering your cleaning services on Dayra. Connect with customers who need exactly what you provide.
                </p>
                <p className="text-base text-ink-400 leading-relaxed">
                    Your local marketplace for trusted cleaning companies across the UAE.
                </p>
            </div>

            {/* Right form panel */}
            <div className="flex-1 flex items-center justify-center px-4 sm:px-8 py-12">
                <div className="w-full max-w-sm">
                    <p className="font-heading text-2xl font-semibold text-ink-900 mb-1">Join as a company</p>
                    <p className="text-sm text-ink-400 mb-8">Offer your services to customers near you</p>

                    {error && (
                        <div className="mb-4 px-4 py-3 bg-uae-red/10 border border-uae-red/20 rounded-lg text-sm text-uae-red">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">

                        <Input
                            id="first_name"
                            label="Company name"
                            type="text"
                            value={form.first_name}
                            onChange={e => setForm({ ...form, first_name: e.target.value })}
                            required
                        />

                        {/* Email & Password */}
                        <Input
                            id="email"
                            label="Email"
                            type="email"
                            value={form.email}
                            onChange={e => setForm({ ...form, email: e.target.value })}
                            placeholder="you@example.com"
                            required
                        />
                        <Input
                            id="password"
                            label="Password"
                            type="password"
                            value={form.password}
                            onChange={e => setForm({ ...form, password: e.target.value })}
                            placeholder="••••••••"
                            required
                        />

                        {/* UAE Phone */}
                        <div className="flex flex-col gap-1">
                            <label htmlFor="phone" className="text-sm font-medium text-ink-700">Phone</label>
                            <div className={`flex items-center border rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-terra-400 focus-within:border-terra-400 transition-all duration-200 ${!phoneValid && phoneDisplay.length > 0 ? 'border-uae-red' : 'border-sand-200'}`}>
                                <span className="flex items-center gap-1.5 px-3 py-2.5 bg-sand-100 border-r border-sand-200 text-sm text-ink-400 shrink-0 select-none whitespace-nowrap">
                                    +971
                                </span>
                                <input
                                    id="phone"
                                    type="tel"
                                    placeholder="5X XXX XXXX"
                                    className="flex-1 px-3 py-2.5 text-sm border-none focus:outline-none bg-white text-ink-900 placeholder:text-ink-400"
                                    value={phoneDisplay}
                                    onChange={handlePhone}
                                    maxLength={9}
                                />
                            </div>
                            {!phoneValid && phoneDisplay.length > 0 && (
                                <p className="text-xs text-uae-red">Enter a 9-digit UAE number starting with 5</p>
                            )}
                        </div>

                        {/* Address */}
                        <div className="flex flex-col gap-3">
                            <p className="text-sm font-medium text-ink-700">
                                Address <span className="text-uae-red">*</span>
                            </p>

                            {/* Address line — full width */}
                            <Input
                                id="address_line"
                                label="Address line"
                                type="text"
                                value={form.address_line}
                                onChange={e => setForm({ ...form, address_line: e.target.value })}
                                placeholder="e.g. 12 Main Street, Downtown"
                                required
                            />

                            {/* City select */}
                            <div className="flex flex-col gap-1">
                                <label htmlFor="location" className="text-sm font-medium text-ink-700">
                                    City <span className="text-uae-red">*</span>
                                </label>
                                <select
                                    id="location"
                                    className="w-full px-4 py-2.5 rounded-md border border-sand-200 bg-white text-ink-900 text-sm focus:outline-none focus:ring-2 focus:ring-terra-400 focus:border-terra-400 transition-all duration-200"
                                    value={form.location}
                                    onChange={e => setForm({ ...form, location: e.target.value })}
                                    required
                                >
                                    <option value="">Select location</option>
                                    {LOCATIONS.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>

                            {/* Building and unit — side by side */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input
                                    id="building"
                                    label="Building name / number"
                                    type="text"
                                    value={form.building}
                                    onChange={e => setForm({ ...form, building: e.target.value })}
                                    placeholder="e.g. Tower A"
                                />
                                <Input
                                    id="unit"
                                    label="Unit / Apt number"
                                    type="text"
                                    value={form.unit}
                                    onChange={e => setForm({ ...form, unit: e.target.value })}
                                    placeholder="e.g. 4B"
                                />
                            </div>
                        </div>

                        {/* Terms & Privacy */}
                        <label className="flex items-start gap-3 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={agreed}
                                onChange={e => setAgreed(e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-sand-200 accent-terra-400 cursor-pointer"
                            />
                            <span className="text-sm text-ink-700 leading-snug">
                                I agree to the{' '}
                                <Link to="/terms" className="text-terra-400 font-medium hover:text-terra-500 transition-colors" target="_blank" rel="noopener noreferrer">
                                    Terms & Conditions
                                </Link>{' '}
                                and{' '}
                                <Link to="/privacy" className="text-terra-400 font-medium hover:text-terra-500 transition-colors" target="_blank" rel="noopener noreferrer">
                                    Privacy Policy
                                </Link>
                            </span>
                        </label>

                        <Button type="submit" variant="primary" loading={loading} className="w-full justify-center">
                            Sign up
                        </Button>
                    </form>

                    <p className="text-sm text-ink-400 mt-6 text-center">
                        Already have an account?{' '}
                        <Link to="/login" className="text-terra-400 font-medium hover:text-terra-500 transition-colors">
                            Sign in
                        </Link>
                    </p>

                    <div className="mt-4 pt-4 border-t border-sand-200 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-ink-400">
                        <span>Looking to get services?</span>
                        <Link to="/signup" className="text-ink-500 hover:text-terra-400 transition-colors font-medium">
                            Sign up as user
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
